import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const days = [
  { 
    word: "الأَحَد", 
    pronunciation: "al-ahad", 
    meaning: "Sunday",
    emoji: "🌅"
  },
  { 
    word: "الإِثْنَيْن", 
    pronunciation: "al-ithnayn", 
    meaning: "Monday",
    emoji: "🌙"
  },
  { 
    word: "الثُّلَاثَاء", 
    pronunciation: "ath-thulatha", 
    meaning: "Tuesday",
    emoji: "⭐"
  },
  { 
    word: "الأَرْبِعَاء", 
    pronunciation: "al-arbi'a", 
    meaning: "Wednesday",
    emoji: "🌍"
  },
  { 
    word: "الخَمِيس", 
    pronunciation: "al-khamis", 
    meaning: "Thursday",
    emoji: "🌠"
  },
  { 
    word: "الجُمُعَة", 
    pronunciation: "al-jumu'ah", 
    meaning: "Friday",
    emoji: "🕌"
  },
  { 
    word: "السَّبْت", 
    pronunciation: "as-sabt", 
    meaning: "Saturday",
    emoji: "🌟"
  }
];

function DaysLesson() {
  const { user } = useUser();
  const [selectedDay, setSelectedDay] = useState(null);
  const [clickedDays, setClickedDays] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.days || 0;
        if (learnedCount > 0) {
          setClickedDays(new Set(days.slice(0, learnedCount).map(d => d.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (dayObj) => {
    setSelectedDay(dayObj);
    const updated = new Set(clickedDays).add(dayObj.word);
    setClickedDays(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'days', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(dayObj.word, "Arabic Male");
  };

  const progress = (clickedDays.size / days.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic h-full bg-gradient-to-br from-blue-50 to-white overflow-y-auto">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-blue-800">أيام الأسبوع</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-green-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedDays.size} من {days.length} يوم</p>

      <AnimatePresence>
        {selectedDay && (
          <motion.div
            key="day-info"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-lg sm:text-xl text-green-700 mb-6"
          >
            <p>النطق: <strong>{selectedDay.pronunciation}</strong></p>
            <p>المعنى: <strong>{selectedDay.meaning}</strong></p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
        {days.map((dayObj, index) => {
          const learned = clickedDays.has(dayObj.word);
          return (
            <button
              key={index}
              onClick={() => handleClick(dayObj)}
              className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                learned ? "bg-green-100 text-green-700" : "bg-white text-blue-800 hover:bg-blue-100"
              }`}
            >
              <div className="text-3xl mb-2">{dayObj.emoji}</div>
              {dayObj.word}
            </button>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="flex justify-center mb-6">
        <SaveButton
          lessonType="days"
          progressData={clickedDays.size}
        />
      </div>

      {/* Additional Information */}
      <div className="max-w-2xl mx-auto mt-8 p-4 bg-white rounded-xl shadow-md">
        <h3 className="text-xl font-bold text-blue-800 mb-4">ملاحظات مهمة</h3>
        <ul className="text-right text-gray-700 space-y-2">
          <li>• يبدأ الأسبوع في معظم الدول العربية يوم الأحد</li>
          <li>• يوم الجمعة هو يوم العطلة الأسبوعية في الدول الإسلامية</li>
          <li>• كلمة "يوم" تعني "day" في اللغة العربية</li>
        </ul>
      </div>

      {/* ResponsiveVoice Script */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default DaysLesson; 